import { useState } from 'react';
import type { ManuscriptDocument } from '../../lib/pdf';
import type { PatternRunResult } from '../../lib/patterns';
import type { ReviewerCandidate, ReviewerSearchResult } from '../../lib/apis';

interface Props {
  manuscript: ManuscriptDocument | null;
  analysis: PatternRunResult | null;
  reviewers: ReviewerSearchResult | null;
  apiState: 'pending' | 'ready' | 'failed';
  selectedKeys: Set<string>;
  manualReviewers: ReviewerCandidate[];
  onToggleSelect: (key: string) => void;
  onAddManual: (candidate: ReviewerCandidate) => void;
  onRemoveManual: (key: string) => void;
}

export function ReviewerSearchPanel({
  manuscript,
  analysis,
  reviewers,
  apiState,
  selectedKeys,
  manualReviewers,
  onToggleSelect,
  onAddManual,
  onRemoveManual,
}: Props) {
  if (!manuscript || !analysis) {
    return (
      <section className="ms-card">
        <div className="ms-ribbon ms-ribbon--emerald">
          <h2>Reviewer suggestions</h2>
        </div>
        <p className="text-xs leading-relaxed text-slate-600">
          Candidate reviewers from <strong>OpenAlex</strong> and <strong>PubMed</strong>,
          queried on the manuscript&rsquo;s author-declared keywords (when present) and
          salient topic terms. A conflict-of-interest filter removes the manuscript&rsquo;s
          own authors and institutions. Only topic keywords leave your device — never the
          manuscript text. Add or remove candidates; your selection is saved with the skim.
        </p>
      </section>
    );
  }

  const topKeywords = analysis.keywords.slice(0, 8);
  const candidates = reviewers?.candidates ?? [];

  return (
    <section className="ms-card">
      <div className="ms-ribbon ms-ribbon--emerald">
        <h2>
          Reviewer suggestions
          {selectedKeys.size + manualReviewers.length > 0 && (
            <span className="ml-1 text-xs font-normal text-emerald-700/80">
              ({selectedKeys.size + manualReviewers.length} selected)
            </span>
          )}
        </h2>
      </div>

      {topKeywords.length > 0 && (
        <div className="mt-1">
          <p className="ms-eyebrow">Search keywords</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {topKeywords.map((k) => (
              <span
                key={k.term}
                className={`ms-chip ${k.kind === 'author' ? 'ms-chip-brand' : ''}`}
                title={`${k.kind} · score ${k.score.toFixed(1)}`}
              >
                {k.term}
              </span>
            ))}
          </div>
        </div>
      )}

      {apiState === 'pending' && (
        <p className="mt-2 text-xs text-slate-500">Querying OpenAlex and PubMed…</p>
      )}
      {apiState === 'ready' && reviewers?.degraded && (
        <p className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900">
          Both OpenAlex and PubMed were unreachable. Add candidates manually below.
        </p>
      )}
      {apiState === 'ready' && reviewers?.partial && (
        <p className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900">
          One of OpenAlex / PubMed failed. Candidate list is partial.
        </p>
      )}

      {candidates.length > 0 && (
        <ul className="mt-2 space-y-1">
          {candidates.slice(0, 12).map((c) => {
            const selected = selectedKeys.has(c.key);
            return (
              <li
                key={c.key}
                className={`rounded border px-2 py-1 text-[11px] ${selected ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200'}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <label className="flex flex-1 cursor-pointer items-start gap-2">
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => onToggleSelect(c.key)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-medium text-slate-800">{c.name}</span>
                        <span className="text-[10px] uppercase tracking-wide text-slate-400">
                          {c.source}
                        </span>
                      </div>
                      {c.affiliation && <p className="mt-0.5 text-slate-600">{c.affiliation}</p>}
                      <p className="mt-0.5 italic text-slate-500">
                        {c.relevantPaperTitle}
                        {c.relevantPaperYear ? ` (${c.relevantPaperYear})` : ''}
                      </p>
                      {c.orcid && (
                        <p className="mt-0.5 font-mono text-[10px] text-slate-400">
                          ORCID {c.orcid}
                        </p>
                      )}
                    </div>
                  </label>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {apiState === 'ready' && candidates.length === 0 && !reviewers?.degraded && (
        <p className="mt-2 text-xs text-slate-500">
          No candidate reviewers returned from search. Add some manually below.
        </p>
      )}

      <ManualAddRow onAdd={onAddManual} />

      {manualReviewers.length > 0 && (
        <div className="mt-2">
          <p className="text-[10px] uppercase tracking-wide text-slate-500">Manually added</p>
          <ul className="mt-1 space-y-1">
            {manualReviewers.map((m) => (
              <li
                key={m.key}
                className="flex items-start justify-between rounded border border-slate-200 px-2 py-1 text-[11px]"
              >
                <div className="flex-1">
                  <p className="font-medium text-slate-800">{m.name}</p>
                  {m.affiliation && <p className="text-slate-600">{m.affiliation}</p>}
                  {m.email && (
                    <p className="font-mono text-[10px] text-slate-500">{m.email}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveManual(m.key)}
                  className="text-[10px] text-slate-400 hover:text-red-600"
                >
                  remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

interface ManualAddProps {
  onAdd: (candidate: ReviewerCandidate) => void;
}

function ManualAddRow({ onAdd }: ManualAddProps) {
  const [name, setName] = useState('');
  const [affiliation, setAffiliation] = useState('');
  const [email, setEmail] = useState('');

  function submit() {
    if (name.trim().length < 2) return;
    onAdd({
      key: `manual:${crypto.randomUUID()}`,
      name: name.trim(),
      affiliation: affiliation.trim() || null,
      orcid: null,
      email: email.trim() || null,
      source: 'manual',
      relevantPaperTitle: '(manual addition)',
      relevantPaperDoi: null,
      relevantPaperYear: null,
    });
    setName('');
    setAffiliation('');
    setEmail('');
  }

  return (
    <div className="mt-2 rounded border border-dashed border-slate-300 p-2">
      <p className="text-[10px] uppercase tracking-wide text-slate-500">Add a reviewer from your network</p>
      <div className="mt-1 grid grid-cols-3 gap-1">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          className="rounded border border-slate-200 px-2 py-1 text-xs"
        />
        <input
          value={affiliation}
          onChange={(e) => setAffiliation(e.target.value)}
          placeholder="Affiliation"
          className="rounded border border-slate-200 px-2 py-1 text-xs"
        />
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email (optional)"
          className="rounded border border-slate-200 px-2 py-1 text-xs"
        />
      </div>
      <div className="mt-1 flex justify-end">
        <button
          type="button"
          onClick={submit}
          disabled={name.trim().length < 2}
          className="rounded bg-slate-700 px-2 py-0.5 text-[11px] text-white disabled:bg-slate-300"
        >
          Add
        </button>
      </div>
    </div>
  );
}
